import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import type { TicketStatusDialog } from '@/pages/Ticket/Ticket.types';

import {
  DEFAULT_OUTCOME_BY_RESOLUTION,
  OUTCOME_HELP_TEXT,
  OUTCOME_OPTIONS,
  REMEDIATION_ELIGIBLE_RESOLUTION_IDS,
  SOC_CLOSE_HELP_TEXT,
  TicketThreatOutcome
} from './TicketStatusDialogThreat.constants';

const TicketStatusThreatOutcomePicker = ({
  resolution
}: {
  resolution?: string;
}) => {
  const { control, setValue } = useFormContext<TicketStatusDialog>();
  const outcome = useWatch({ control, name: 'outcome' });
  const isEligibleForRemediation =
    !!resolution && REMEDIATION_ELIGIBLE_RESOLUTION_IDS.includes(resolution);

  // Whenever the resolution changes, reset to its sensible default outcome.
  // The user can still override it with the cards below.
  useEffect(() => {
    if (!resolution) return;
    setValue(
      'outcome',
      isEligibleForRemediation
        ? DEFAULT_OUTCOME_BY_RESOLUTION[resolution]
        : TicketThreatOutcome.Close
    );
  }, [resolution]);

  if (!resolution) {
    return null;
  }

  if (!isEligibleForRemediation) {
    return <Alert severity="info">{SOC_CLOSE_HELP_TEXT}</Alert>;
  }

  return (
    <RadioGroup
      name="outcome-radio-group"
      value={outcome ?? ''}
      onChange={(e) =>
        setValue('outcome', e.target.value as TicketThreatOutcome)
      }
    >
      <Stack direction="row" spacing={1.5}>
        {OUTCOME_OPTIONS.map((option) => (
          <Paper
            key={option.value}
            variant="outlined"
            sx={{
              flex: 1,
              borderWidth: 2,
              borderColor: outcome === option.value ? 'primary.main' : 'divider'
            }}
          >
            <FormControlLabel
              value={option.value}
              control={<Radio />}
              sx={{
                alignItems: 'flex-start',
                width: '100%',
                m: 0,
                py: 1,
                px: 1.5
              }}
              label={
                <Box>
                  <Typography sx={{ fontWeight: 'bold' }}>
                    {option.label}
                  </Typography>
                  <FormHelperText sx={{ mt: -0.05 }}>
                    {OUTCOME_HELP_TEXT[resolution][option.value]}
                  </FormHelperText>
                </Box>
              }
            />
          </Paper>
        ))}
      </Stack>
    </RadioGroup>
  );
};

export default TicketStatusThreatOutcomePicker;
